import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import GlassSpinner from "../../components/GlassSpinner";
import { useEmpresa } from "../../context/EmpresaContext";
import {
  getEventos, getEntradas, registerEntradas, eliminarEntrada,
  reenviarEntrada, reenviarEntradasLote, getEmpresaConfig,
} from "../../services/sininApi";

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const defaultCompanyLogo = key => key ? `https://buckettree.s3.amazonaws.com/${key}` : "";

const parseEventDateTime = (fecha, hora = "") => {
  const normalized = String(fecha || "").trim().replaceAll("/", "-");
  if (!normalized) return null;
  const value = hora ? `${normalized}T${String(hora).slice(0, 5)}` : normalized;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const eventEnded = ev => {
  const end = parseEventDateTime(ev?.fechaFinEvento || ev?.fechaEvento, ev?.horaFinEvento || "23:59");
  return Boolean(end && new Date() > end);
};

const formatIngreso = value => {
  if (!value) return "—";
  const [fecha = "", hora = ""] = String(value).trim().split(/\s+/);
  const partes = fecha.replaceAll("-", "/").split("/");
  const fechaVisible = partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : fecha;
  return `${fechaVisible}${hora ? ` ${hora.slice(0, 5)}` : ""}`;
};

function Bar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
        <span>{label}</span>
        <span style={{ fontWeight: "700" }}>{value} ({pct}%)</span>
      </div>
      <div style={{ background: "#e9ecef", borderRadius: 6, height: 10 }}>
        <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 6, transition: "width .4s" }} />
      </div>
    </div>
  );
}

const EMPTY_FORM = { first_name: "", last_name: "", dni: "", email: "", quantity: "1", sexo: "" };

export default function VendedorPanel() {
  const navigate             = useNavigate();
  const { empresa, logout }  = useEmpresa();
  const [eventoActivo, setEventoActivo] = useState(null);
  const membresias           = Array.isArray(empresa?.membresias) && empresa.membresias.length > 0
    ? empresa.membresias
    : (empresa?.empresaId ? [empresa] : []);
  const vendedorLoginPath    = membresias[0]?.empresaId
    ? `/${String(membresias[0].empresaId).toUpperCase()}`
    : "/ADMIN";
  const marcaActiva          = eventoActivo?._membresia || null;
  const GOLD                 = marcaActiva?.colorTicket || "#dfa66b";
  const FONDO                = marcaActiva?.colorFondo || "#1a1a1a";
  const FUENTE               = marcaActiva?.colorFuente || "#ffffff";
  const empresaId            = eventoActivo?.empresaId || marcaActiva?.empresaId;
  const rrppDni              = empresa?.dni;
  const nombre               = `${empresa?.first_name || ""} ${empresa?.last_name || ""}`.trim();
  const ticketNombre         = marcaActiva?.ticketNombre || "Ticket";
  const ticketNombrePlural   = marcaActiva?.ticketNombrePlural || "Tickets";
  const vendedorTitulo       = marcaActiva?.vendedorTitulo || "Panel de vendedor";
  const vendedorAccion       = marcaActiva?.vendedorAccion || `Generar ${ticketNombre.toLowerCase()}`;
  const mailEnabled          = marcaActiva?.mailEnabled === true;

  const [entradas, setEntradas]   = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [eventos, setEventos]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [sending, setSending]     = useState(false);
  const [bulkWorking, setBulkWorking] = useState("");
  const [tab, setTab]             = useState("cortesia");
  const [sortConfig, setSortConfig]     = useState({ key: null, dir: "asc" });
  const [busqueda, setBusqueda]         = useState("");
  const [columnFilters, setColumnFilters] = useState({
    numeroTicket: "", first_name: "", last_name: "", dni: "",
    tanda: "", tipoPago: "", used: "",
  });
  const [selectedHashes, setSelectedHashes] = useState([]);

  const [cortesiaForm, setCortesiaForm]     = useState(EMPTY_FORM);
  const [cortesiaTanda, setCortesiaTanda]   = useState("");
  const [cortesiaExtras, setCortesiaExtras] = useState({});
  const [ticketsGenerados, setTicketsGenerados] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [dialogInput, setDialogInput] = useState("");
  const sharingGeneratedRef = useRef(false);
  const setCF = k => e => setCortesiaForm(p => ({ ...p, [k]: e.target.value }));

  const notify = (type, title, message, autoClose = false) => {
    setNotice({ type, title, message, autoClose });
  };

  useEffect(() => {
    if (!notice?.autoClose) return;
    const timer = setTimeout(() => setNotice(null), 2300);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (empresa?.role !== "rrpp" || membresias.length === 0) {
      navigate("/ADMIN");
      return;
    }
    setLoading(true);
    Promise.all(membresias.map(async membresia => {
      try {
        const [data, configData] = await Promise.all([
          getEventos(membresia.empresaId, membresia.loginType || "2"),
          getEmpresaConfig(membresia.empresaId),
        ]);
        const membresiaConfigurada = {
          ...membresia,
          ...(configData?.empresa || {}),
          mailEnabled: configData?.empresa?.mailEnabled === true,
        };
        const idsAsignados = Array.isArray(membresia.eventos) ? membresia.eventos : [];
        return (Array.isArray(data) ? data : [])
          .filter(ev => idsAsignados.includes(ev.idEvento))
          .filter(ev => !eventEnded(ev))
          .map(ev => ({
            ...ev,
            empresaId: ev.empresaId || membresia.empresaId,
            _membresia: membresiaConfigurada,
          }));
      } catch {
        return [];
      }
    }))
      .then(grupos => {
        const asignados = grupos.flat();
        setEventos(asignados);
        if (asignados.length === 1) setEventoActivo(asignados[0]);
      })
      .finally(() => setLoading(false));
  }, [empresa, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async ({ showLoader = true } = {}) => {
    if (!eventoActivo) return [];
    if (showLoader) setLoading(true);
    try {
      const res = await getEntradas(empresaId, {
        idEvento:  eventoActivo.idEvento,
        createdBy: rrppDni,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      setEntradas(items);
      return items;
    } catch {
      setEntradas([]);
      return [];
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [empresaId, rrppDni, eventoActivo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === "lista" && eventoActivo) load();
  }, [tab, eventoActivo, load]);

  useEffect(() => {
    let list = [...entradas];
    const q = busqueda.trim().toLowerCase();
    if (q) {
      list = list.filter(i =>
        i.dni?.toLowerCase().includes(q) ||
        `${i.first_name} ${i.last_name}`.toLowerCase().includes(q)
      );
    }
    list = list.filter(ticket => {
      const values = {
        numeroTicket: ticketPosition(ticket),
        first_name: ticket.first_name || "",
        last_name: ticket.last_name || "",
        dni: ticket.dni || "",
        tanda: ticket.tanda || "",
        tipoPago: ticket.tipoPago || "",
        used: ticket.used ? "si ingresó utilizado" : "no pendiente",
      };
      return Object.entries(columnFilters).every(([key, value]) =>
        !value.trim() || String(values[key] || "").toLowerCase().includes(value.trim().toLowerCase())
      );
    });
    if (sortConfig.key) {
      list.sort((a, b) => {
        const va = String(a[sortConfig.key] || "").toLowerCase();
        const vb = String(b[sortConfig.key] || "").toLowerCase();
        return sortConfig.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    setFiltered(list);
  }, [busqueda, entradas, sortConfig, columnFilters]);

  useEffect(() => {
    const activos = new Set(entradas.map(ticket => ticket.hashSeguro));
    setSelectedHashes(previous => previous.filter(hash => activos.has(hash)));
  }, [entradas]);

  const toggleSort = key => setSortConfig(p => ({ key, dir: p.key === key && p.dir === "asc" ? "desc" : "asc" }));
  const arrow      = key => sortConfig.key === key ? (sortConfig.dir === "asc" ? " ▲" : " ▼") : "";

  const ticketPosition = ticket => {
    const numero = ticket.numeroTicket || "1";
    const total = ticket.cantidadTotal || "1";
    return `${numero} de ${total}`;
  };

  const ticketShareText = ticket =>
    `${ticketNombre} ${ticketPosition(ticket)} de ${ticket.first_name || ""} ${ticket.last_name || ""} — ${eventoActivo?.name || eventoActivo?.nombre || ""}`.trim();

  const abrirWhatsApp = texto => {
    const url = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    const nuevaVentana = window.open(url, "_blank");
    if (nuevaVentana) nuevaVentana.opener = null;
    else window.location.assign(url);
  };

  const navegadorCompartePdfs = () => {
    if (!navigator.share || !navigator.canShare || typeof File === "undefined") return false;
    try {
      const prueba = new File(["PDF"], "ticket.pdf", { type: "application/pdf" });
      return navigator.canShare({ files: [prueba] });
    } catch {
      return false;
    }
  };

  const compartirPdfPorWhatsApp = async ({ url, nombre, texto }) => {
    if (!url) return notify("error", "No se pudo compartir", "No encontramos el enlace de este ticket.");

    if (!navegadorCompartePdfs()) {
      abrirWhatsApp(`${texto}\n${url}`);
      notify("success", "WhatsApp abierto", "El enlace seguro al PDF está preparado. En WhatsApp presioná Enviar.", true);
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("No se pudo preparar el PDF.");
      const archivo = new File([await response.blob()], nombre, { type: "application/pdf" });
      await navigator.share({ files: [archivo] });
    } catch (e) {
      if (e?.name !== "AbortError") {
        abrirWhatsApp(`${texto}\n${url}`);
        notify("warning", "Se compartirá como enlace", "No pudimos adjuntar el archivo; abrimos WhatsApp con el PDF enlazado.", true);
      }
    }
  };

  const compartirWhatsApp = async ticket => {
    await compartirPdfPorWhatsApp({
      url: ticket.pdf_url,
      nombre: nombreArchivoTicket(ticket, 0),
      texto: ticketShareText(ticket),
    });
  };

  const enviarEmail = async (ticket, destino) => {
    if (!destino) return;
    setDialog(null);
    setLoading(true);
    try {
      const res = await reenviarEntrada(empresaId, ticket.hashSeguro, rrppDni, destino);
      if (res?.error) throw new Error(res.error);
      notify("success", "Ticket enviado", `El email fue enviado correctamente a ${destino}.`);
      await load();
    } catch (e) {
      notify("error", "No se pudo enviar", e?.response?.data?.error || e.message || "Revisá la configuración de Gmail.");
      setLoading(false);
    }
  };

  const compartirEmail = ticket => {
    if (ticket.email) return enviarEmail(ticket, ticket.email);
    setDialogInput("");
    setDialog({
      type: "input",
      title: "Enviar ticket por email",
      message: `Ingresá el email para enviar el ${ticketNombre.toLowerCase()} ${ticketPosition(ticket)}.`,
      confirmLabel: "Enviar email",
      onConfirm: value => enviarEmail(ticket, value),
    });
  };

  const ticketsParaCompartir = () => {
    const elegidos = new Set(selectedHashes);
    return filtered.filter(ticket => elegidos.has(ticket.hashSeguro) && ticket.pdf_url);
  };

  const nombreArchivoTicket = (ticket, index) => {
    const persona = `${ticket.first_name || ""}_${ticket.last_name || ""}`
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "");
    const posicion = `${ticket.numeroTicket || index + 1}_de_${ticket.cantidadTotal || 1}`;
    return `${persona || "ticket"}_${ticket.dni || index + 1}_${posicion}.pdf`;
  };

  const obtenerArchivos = async tickets => {
    const resultados = await Promise.all(tickets.map(async (ticket, index) => {
      const response = await fetch(ticket.pdf_url);
      if (!response.ok) throw new Error(`No se pudo descargar ${ticketPosition(ticket)}`);
      return {
        ticket,
        nombre: nombreArchivoTicket(ticket, index),
        blob: await response.blob(),
      };
    }));
    return resultados;
  };

  const descargarUrlsComoZip = async (urls, nombreBase, persona = "ticket") => {
    const zip = new JSZip();
    const archivos = await Promise.all(urls.map(async (url, index) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`No se pudo descargar el ticket ${index + 1}`);
      return { nombre: `${persona}_${index + 1}_de_${urls.length}.pdf`, blob: await response.blob() };
    }));
    archivos.forEach(archivo => zip.file(archivo.nombre, archivo.blob));
    const contenido = await zip.generateAsync({ type: "blob" });
    const zipUrl = URL.createObjectURL(contenido);
    const link = document.createElement("a");
    link.href = zipUrl;
    link.download = `${nombreBase}.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(zipUrl);
    return archivos.length;
  };

  const descargarTodos = async () => {
    const tickets = ticketsParaCompartir();
    if (!tickets.length) return notify("warning", "Seleccioná tickets", "Marcá las casillas de los tickets que querés descargar.");
    setBulkWorking("download");
    try {
      const archivos = await obtenerArchivos(tickets);
      const zip = new JSZip();
      archivos.forEach(archivo => zip.file(archivo.nombre, archivo.blob));
      const contenido = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(contenido);
      const link = document.createElement("a");
      const evento = (eventoActivo?.name || eventoActivo?.nombre || "evento")
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "");
      link.href = url;
      link.download = `${evento || "evento"}_tickets.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notify("success", "Descarga preparada", `Se descargaron ${archivos.length} tickets en un archivo ZIP.`, true);
    } catch (e) {
      notify("error", "No se pudieron descargar", e.message || "Intentá nuevamente.");
    } finally {
      setBulkWorking("");
    }
  };

  const compartirTodosWhatsApp = async () => {
    const tickets = ticketsParaCompartir();
    if (!tickets.length) return notify("warning", "Seleccioná tickets", "Marcá las casillas de los tickets que querés compartir.");

    const texto = `${ticketNombrePlural} — ${eventoActivo?.name || eventoActivo?.nombre || ""}`;
    const links = tickets.map((ticket, index) =>
      `${index + 1}. ${ticketShareText(ticket)}\n${ticket.pdf_url}`
    ).join("\n\n");

    if (!navigator.share || !navigator.canShare) {
      abrirWhatsApp(`${texto}\n\n${links}`);
      return;
    }

    setBulkWorking("whatsapp");
    try {
      const archivos = await obtenerArchivos(tickets);
      const files = archivos.map(archivo => new File([archivo.blob], archivo.nombre, { type: "application/pdf" }));

      if (navigator.share && navigator.canShare?.({ files })) {
        await navigator.share({ files });
      } else {
        abrirWhatsApp(`${texto}\n\n${links}`);
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        abrirWhatsApp(`${texto}\n\n${links}`);
      }
    } finally {
      setBulkWorking("");
    }
  };

  const enviarTodosEmail = async destino => {
    const tickets = ticketsParaCompartir();
    if (!tickets.length || !destino) return;
    setDialog(null);
    setBulkWorking("email");
    try {
      const res = await reenviarEntradasLote(
        empresaId,
        tickets.map(ticket => ticket.hashSeguro),
        rrppDni,
        destino,
      );
      if (res?.error) throw new Error(res.error);
      notify("success", "Tickets enviados", `Se enviaron ${res?.cantidad || tickets.length} PDFs juntos a ${destino}.`);
      await load();
    } catch (e) {
      notify("error", "No se pudieron enviar", e?.response?.data?.error || e.message || "Revisá la configuración de Gmail.");
    } finally {
      setBulkWorking("");
    }
  };

  const pedirEmailParaTodos = () => {
    if (!ticketsParaCompartir().length) {
      return notify("warning", "Seleccioná tickets", "Marcá las casillas de los tickets que querés enviar.");
    }
    setDialogInput("");
    setDialog({
      type: "input",
      title: "Enviar todos por email",
      message: `Ingresá el email que recibirá los ${ticketsParaCompartir().length} tickets juntos.`,
      confirmLabel: "Enviar todos",
      onConfirm: enviarTodosEmail,
    });
  };

  const toggleTicketSelection = hash => {
    setSelectedHashes(previous =>
      previous.includes(hash) ? previous.filter(value => value !== hash) : [...previous, hash]
    );
  };

  const todosVisiblesSeleccionados = filtered.length > 0 &&
    filtered.every(ticket => selectedHashes.includes(ticket.hashSeguro));

  const toggleTodosVisibles = () => {
    const visibles = filtered.map(ticket => ticket.hashSeguro);
    setSelectedHashes(previous => {
      if (todosVisiblesSeleccionados) return previous.filter(hash => !visibles.includes(hash));
      return Array.from(new Set([...previous, ...visibles]));
    });
  };

  const limpiarFiltros = () => {
    setBusqueda("");
    setColumnFilters({
      numeroTicket: "", first_name: "", last_name: "", dni: "",
      tanda: "", tipoPago: "", used: "",
    });
  };

  const descargarTodosGenerados = async () => {
    if (!ticketsGenerados?.urls?.length) return;
    setBulkWorking("generated-download");
    try {
      const evento = (eventoActivo?.name || eventoActivo?.nombre || "evento")
        .replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
      const persona = (ticketsGenerados.nombre || "ticket")
        .replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
      const cantidad = await descargarUrlsComoZip(
        ticketsGenerados.urls,
        `${evento}_${persona}_tickets`,
        persona,
      );
      notify("success", "Descarga preparada", `Se descargaron ${cantidad} tickets juntos.`, true);
    } catch (e) {
      notify("error", "No se pudieron descargar", e.message || "Intentá nuevamente.");
    } finally {
      setBulkWorking("");
    }
  };

  const compartirTodosGeneradosWhatsApp = async () => {
    if (!ticketsGenerados?.urls?.length || sharingGeneratedRef.current) return;
    const urls = Array.from(new Set(ticketsGenerados.urls.filter(Boolean)));
    const texto = `${ticketNombrePlural} de ${ticketsGenerados.nombre} — ${eventoActivo?.name || eventoActivo?.nombre || ""}`;
    const links = urls.map((url, index) => `${index + 1}. ${url}`).join("\n");
    sharingGeneratedRef.current = true;
    setBulkWorking("generated-whatsapp");
    try {
      if (navigator.share && navigator.canShare) {
        const persona = (ticketsGenerados.nombre || "ticket")
          .replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
        const files = await Promise.all(urls.map(async (url, index) => {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`No se pudo preparar el ticket ${index + 1}`);
          return new File(
            [await response.blob()],
            `${persona}_${index + 1}_de_${urls.length}.pdf`,
            { type: "application/pdf" },
          );
        }));
        if (navigator.canShare({ files })) {
          // Compartir solamente archivos: algunos Android cuentan title y text
          // como elementos extra (3 PDFs terminaban apareciendo como 5 ítems).
          await navigator.share({ files });
          return;
        }
      }
      abrirWhatsApp(`${texto}\n\n${links}`);
    } catch (e) {
      if (e?.name !== "AbortError") abrirWhatsApp(`${texto}\n\n${links}`);
    } finally {
      sharingGeneratedRef.current = false;
      setBulkWorking("");
    }
  };

  const eliminarTicket = async ticket => {
    setDialog(null);
    setLoading(true);
    try {
      const res = await eliminarEntrada(empresaId, ticket.hashSeguro, rrppDni);
      if (res?.error) throw new Error(res.error);
      await load();
      notify(
        "success",
        "Ticket eliminado",
        res.mailSent
          ? `${ticketNombre} ${ticketPosition(ticket)} y su PDF fueron eliminados. Se avisó por email que quedó inhabilitado.`
          : `${ticketNombre} ${ticketPosition(ticket)} y su PDF fueron eliminados. No se pudo avisar por email: ${res.mailWarning || "sin detalle"}.`
      );
    } catch (e) {
      notify("error", "No se pudo eliminar", e?.response?.data?.error || e.message || "Intentá nuevamente.");
      setLoading(false);
    }
  };

  const handleEliminarTicket = ticket => {
    if (ticket.used) {
      return notify("warning", "Ticket ya utilizado", "No se puede eliminar porque el QR ya fue escaneado.");
    }
    setDialog({
      type: "danger",
      title: `Eliminar ${ticketNombre.toLowerCase()} ${ticketPosition(ticket)}`,
      message: `${ticket.first_name} ${ticket.last_name}. Se eliminarán definitivamente el registro y su PDF. Los demás tickets conservarán su numeración.`,
      confirmLabel: "Sí, eliminar",
      onConfirm: () => eliminarTicket(ticket),
    });
  };

  const generarCortesiaConfirmada = async (payload, persona) => {
    setDialog(null);
    setSending(true);
    try {
      const res = await registerEntradas([payload]);

      if (res?.error) throw new Error(res.error);

      const resultados = Array.isArray(res?.resultados) ? res.resultados : [];
      const error = resultados.find(r => !r.ok);
      if (error) {
        notify(
          error.duplicate ? "warning" : "error",
          error.duplicate ? "DNI DUPLICADO" : "No se pudo generar",
          error.error || "Ocurrió un error desconocido."
        );
      } else {
        const cantidadAnterior = entradas.length;
        setTicketsGenerados({
          nombre: persona,
          urls: Array.from(new Set(resultados.map(r => r.pdf_url).filter(Boolean))),
        });
        setCortesiaForm(EMPTY_FORM);
        setCortesiaTanda("");
        setCortesiaExtras({});
        // El índice por evento de DynamoDB puede reflejar una escritura unos
        // instantes después. Reintentamos para que "Mis tickets" no quede viejo.
        await wait(450);
        const actualizados = await load({ showLoader: false });
        if (actualizados.length < cantidadAnterior + Number(payload.quantity || 1)) {
          await wait(1000);
          await load({ showLoader: false });
        }
      }
    } catch (e) {
      const apiError = e?.response?.data?.error || e?.response?.data?.message;
      const message = apiError || e.message || "Ocurrió un error desconocido.";
      if (String(message).includes("FIN_EVENTO")) {
        notify("warning", "FIN DEL EVENTO", "Tu usuario fue desactivado. No se creó ningún ticket y la sesión se cerrará.");
        window.setTimeout(() => {
          logout();
          navigate(vendedorLoginPath, { replace: true });
        }, 2200);
      } else {
        notify("error", "No se pudo generar", message);
      }
    }
    setSending(false);
  };

  const handleCortesia = () => {
    const { first_name, last_name, dni, email, quantity } = cortesiaForm;
    if (!first_name || !last_name || !dni) {
      return notify("warning", "Faltan datos", "Completá nombre, apellido y DNI.");
    }
    if (!empresaId || !eventoActivo?.idEvento) {
      return notify("error", "Evento no identificado", "Volvé a seleccionar el evento e intentá nuevamente.");
    }
    if (eventEnded(eventoActivo)) {
      notify("warning", "EVENTO FINALIZADO", "Este evento ya terminó. No se creó ningún ticket y se cerrará la sesión.");
      window.setTimeout(() => {
        logout();
        navigate(vendedorLoginPath, { replace: true });
      }, 2200);
      return;
    }

    const tandas = eventoActivo?.tandas || [];
    const tanda  = tandas.find(t => t.id === cortesiaTanda) || tandas[0];
    if (!tanda) return notify("warning", "Sin tipos de entrada", "El evento no tiene entradas configuradas.");

    const cantidad = Number(quantity);
    if (!Number.isInteger(cantidad) || cantidad < 1 || cantidad > 10) {
      return notify("warning", "Cantidad inválida", "Ingresá una cantidad entera entre 1 y 10.");
    }

    const persona = `${first_name} ${last_name}`.trim();
    const eventoNombre = eventoActivo?.name || eventoActivo?.nombre || "";
    const payload = {
      empresaId,
      idEvento: eventoActivo.idEvento,
      nombre: first_name,
      apellido: last_name,
      dni: dni.trim().toUpperCase(),
      email: mailEnabled ? email : "",
      sexo: cortesiaForm.sexo,
      name: eventoNombre,
      tanda: tanda.label,
      tandaId: tanda.id,
      tipoPago: tanda.tipo,
      quantity: cantidad,
      price: tanda.tipo === "PAGO" ? (tanda.precio || 0) : 0,
      createdBy: rrppDni,
      extras: { ...cortesiaExtras },
    };

    setDialog({
      type: "tickets",
      title: `Confirmar ${ticketNombrePlural.toLowerCase()}`,
      quantity: cantidad,
      details: [
        ["Evento", eventoNombre],
        ["Nombre", persona],
        ["DNI", dni.trim().toUpperCase()],
        ["Tipo", tanda.label],
        ["Modalidad", tanda.tipo === "PAGO" ? "Pago" : "Cortesía"],
        ...(mailEnabled && email ? [["Email", email]] : []),
      ],
      confirmLabel: `Generar ${cantidad}`,
      onConfirm: () => generarCortesiaConfirmada(payload, persona),
    });
  };

  const stats = {
    total:      entradas.length,
    ingresaron: entradas.filter(e => e.used).length,
    pendientes: entradas.filter(e => !e.used).length,
    cortesias:  entradas.filter(e => e.tipoPago === "CORTESIA").length,
  };

  const styles = makeStyles(GOLD, FONDO, FUENTE);
  const panelTheme = (
    <style>{`
      .vendedor-panel button,
      .vendedor-panel a[role="button"] {
        border-radius: 25px !important;
        text-transform: uppercase !important;
        letter-spacing: .35px;
      }
      .vendedor-panel h1,
      .vendedor-panel h2,
      .vendedor-panel h3,
      .vendedor-panel .panel-title {
        text-transform: uppercase;
        letter-spacing: .5px;
      }
    `}</style>
  );

  // ── Pantalla de selección de evento ──────────────────────────────────────
  if (!eventoActivo) {
    return (
      <div className="vendedor-panel" style={styles.page}>
        {panelTheme}
        <div style={styles.neutralHeader}>
          <div>
            <span style={{ fontWeight: "800", fontSize: 16 }}>Hola, {nombre || "vendedor"}</span>
            <div style={{ fontSize: 11, opacity: .65, marginTop: 2 }}>Estos son tus eventos activos</div>
          </div>
          <button style={styles.outlineBtn} onClick={() => { logout(); navigate(vendedorLoginPath); }}>Salir</button>
        </div>
        <div style={{ padding: 20, maxWidth: 620, margin: "0 auto" }}>
          <h2 style={{ fontSize: 22, marginBottom: 6 }}>Elegí un evento</h2>
          <p style={{ color: "#777", fontSize: 13, marginBottom: 20 }}>
            El diseño y las opciones cambiarán según la empresa organizadora.
          </p>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <GlassSpinner size={70} background={FONDO} />
            </div>
          ) : eventos.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>📅</div>
              No tenés eventos activos asignados.
            </div>
          ) : eventos.map(ev => (
            <div key={`${ev._membresia.empresaId}-${ev.idEvento}`}
              onClick={() => setEventoActivo(ev)}
              style={{ background: "#fff", borderRadius: 16, padding: 18, marginBottom: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)", cursor: "pointer",
                borderLeft: `6px solid ${ev._membresia.colorTicket || "#dfa66b"}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {(ev._membresia.logoUrl || ev._membresia.logoKey) && (
                  <img
                    src={ev._membresia.logoUrl || defaultCompanyLogo(ev._membresia.logoKey)}
                    alt={ev._membresia.empresaNombre}
                    style={{ width: 48, height: 48, borderRadius: 10, objectFit: "contain", background: ev._membresia.colorFondo || "#222", padding: 5 }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: "700", textTransform: "uppercase", letterSpacing: .7 }}>
                    {ev._membresia.empresaNombre}
                  </div>
                  <div style={{ fontWeight: "800", fontSize: 17, marginTop: 2 }}>{ev.name || ev.nombre}</div>
                  <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{ev.fechaEvento || ev.fecha}</div>
                </div>
                <span style={{ color: ev._membresia.colorTicket || "#dfa66b", fontSize: 24 }}>›</span>
              </div>
              {ev.tandas?.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12, marginLeft: ev._membresia.logoKey ? 60 : 0 }}>
                  {ev.tandas.map(t => (
                    <span key={t.id} style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: "600",
                      background: t.tipo === "CORTESIA" ? "#d1e7dd" : "#fff3cd" }}>
                      {t.label} {t.tipo === "PAGO" ? `$${t.precio}` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="vendedor-panel" style={styles.page}>
      {panelTheme}
      {loading && <div style={styles.loadingOverlay}><GlassSpinner size={80} background={FONDO} /></div>}

      <div style={styles.header}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            {(marcaActiva?.logoUrl || marcaActiva?.logoKey) && (
              <img src={marcaActiva.logoUrl || defaultCompanyLogo(marcaActiva.logoKey)}
                alt={marcaActiva.empresaNombre}
                style={{ width: 42, height: 42, objectFit: "contain", background: "transparent" }} />
            )}
            <div>
              <div style={{ fontWeight: "700", fontSize: 15 }}>{eventoActivo.name || eventoActivo.nombre}</div>
              <div style={{ fontSize: 11, opacity: 0.72 }}>{marcaActiva?.empresaNombre} · {vendedorTitulo} · {nombre || "RRPP"}</div>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.outlineBtn} onClick={() => { setEventoActivo(null); setEntradas([]); }}>← Eventos</button>
          <button style={styles.outlineBtn} onClick={() => { logout(); navigate(vendedorLoginPath); }}>Salir</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", background: "#fff", borderBottom: "1px solid #e0e0e0" }}>
        {[["cortesia",`+ ${vendedorAccion}`],["lista",`Mis ${ticketNombrePlural}`],["stats","Stats"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: "12px 0", border: "none", background: "transparent",
            fontWeight: tab === t ? "700" : "400",
            color: tab === t ? GOLD : "#555",
            borderBottom: tab === t ? `2px solid ${GOLD}` : "2px solid transparent",
            cursor: "pointer", fontSize: 13,
          }}>{l}</button>
        ))}
      </div>

      {/* Chips */}
      <div style={styles.chipsRow}>
        {[["Total", stats.total,"#e9ecef"],["Ingresaron",stats.ingresaron,"#d1e7dd"],
          ["Pendientes",stats.pendientes,"#cfe2ff"],["Cortesías",stats.cortesias,"#f8d7da"]].map(([l,v,bg]) => (
          <div key={l} style={{ ...styles.chip, background: bg }}>
            <span style={{ fontWeight: "800", fontSize: 18 }}>{v}</span>
            <span style={{ fontSize: 11, color: "#555" }}>{l}</span>
          </div>
        ))}
      </div>

      {/* ── CORTESÍA ── */}
      {tab === "cortesia" && (
        <div style={{ padding: 16, maxWidth: 500, margin: "0 auto" }}>
          <div style={styles.section}>

            {/* Tipo de entrada */}
            {(eventoActivo?.tandas || []).length > 0 && (
              <>
                <label style={styles.flabel}>Tipo de entrada *</label>
                <select style={styles.finput} value={cortesiaTanda} onChange={e => setCortesiaTanda(e.target.value)}>
                  <option value="">Seleccioná...</option>
                  {eventoActivo.tandas.map(t => (
                    <option key={t.id} value={t.id}>{t.label}{t.tipo === "PAGO" ? ` ($${t.precio})` : " — Cortesía"}</option>
                  ))}
                </select>
              </>
            )}

            {/* Datos del invitado */}
            {[
              ["first_name", "Nombre *", "Juan"],
              ["last_name", "Apellido *", "Pérez"],
              ["dni", "DNI *", "12345678"],
              ...(mailEnabled ? [["email", "Email (para enviar ticket)", "juan@email.com"]] : []),
            ].map(([k,lbl,ph]) => (
              <React.Fragment key={k}>
                <label style={styles.flabel}>{lbl}</label>
                <input style={styles.finput} value={cortesiaForm[k]} onChange={setCF(k)} placeholder={ph} />
              </React.Fragment>
            ))}

            <label style={styles.flabel}>Sexo *</label>
            <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
              {["Masculino","Femenino","Otro"].map(op => (
                <label key={op} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}>
                  <input type="radio" name="sexo" value={op}
                    checked={cortesiaForm.sexo === op}
                    onChange={e => setCortesiaForm(p => ({ ...p, sexo: e.target.value }))} />
                  {op}
                </label>
              ))}
            </div>

            {/* Campos personalizados del evento */}
            {(eventoActivo?.campos || []).filter(c => c.activo !== false).map(campo => (
              <React.Fragment key={campo.id}>
                <label style={styles.flabel}>{campo.label}</label>
                {campo.tipo === "sinNo" ? (
                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    {["Sí","No"].map(op => (
                      <label key={op} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 14 }}>
                        <input type="radio" name={`campo_${campo.id}`}
                          checked={cortesiaExtras[campo.id] === op}
                          onChange={() => setCortesiaExtras(p => ({ ...p, [campo.id]: op }))} />
                        {op}
                      </label>
                    ))}
                  </div>
                ) : campo.tipo === "dropdown" ? (
                  <select style={styles.finput} value={cortesiaExtras[campo.id] || ""}
                    onChange={e => setCortesiaExtras(p => ({ ...p, [campo.id]: e.target.value }))}>
                    <option value="">Seleccioná...</option>
                    {(campo.opciones || []).map(op => <option key={op} value={op}>{op}</option>)}
                  </select>
                ) : (
                  <input style={styles.finput} value={cortesiaExtras[campo.id] || ""}
                    onChange={e => setCortesiaExtras(p => ({ ...p, [campo.id]: e.target.value }))}
                    placeholder={campo.label} />
                )}
              </React.Fragment>
            ))}

            <label style={styles.flabel}>Cantidad de entradas</label>
            <input style={styles.finput} type="number" min="1" max="10" value={cortesiaForm.quantity} onChange={setCF("quantity")} />

            <button
              style={{ background: "#198754", color: "#fff", border: "none", borderRadius: 8, padding: "12px", width: "100%", marginTop: 16, cursor: "pointer", fontWeight: "700", fontSize: 14, opacity: sending ? 0.7 : 1 }}
              onClick={handleCortesia} disabled={sending}>
              {sending ? "Generando..." : vendedorAccion}
            </button>
          </div>
        </div>
      )}

      {/* ── LISTA ── */}
      {tab === "lista" && (
        <>
          <div style={styles.bulkActions}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: "800", fontSize: 14 }}>ACCIONES PARA SELECCIONADOS</div>
              <div style={{ color: "#777", fontSize: 11, marginTop: 2 }}>
                {selectedHashes.length} seleccionados · {filtered.length} visibles
              </div>
            </div>
            <button style={{ ...styles.bulkBtn, background: GOLD, color: FUENTE }}
              disabled={!!bulkWorking || selectedHashes.length === 0} onClick={descargarTodos}>
              {bulkWorking === "download" ? "Preparando..." : "Descargar selección"}
            </button>
            <button style={{ ...styles.bulkBtn, background: "#25D366", color: "#fff" }}
              disabled={!!bulkWorking || selectedHashes.length === 0} onClick={compartirTodosWhatsApp}>
              {bulkWorking === "whatsapp" ? "Preparando..." : "WhatsApp selección"}
            </button>
            {mailEnabled && (
              <button style={{ ...styles.bulkBtn, background: "#444", color: "#fff" }}
                disabled={!!bulkWorking || selectedHashes.length === 0} onClick={pedirEmailParaTodos}>
                {bulkWorking === "email" ? "Enviando..." : "Email selección"}
              </button>
            )}
          </div>
          <div style={styles.filtersRow}>
            <input style={styles.fi} placeholder="Buscar nombre o DNI..."
              value={busqueda} onChange={e => setBusqueda(e.target.value)} />
            <button style={styles.clearBtn} onClick={limpiarFiltros}>Limpiar filtros</button>
          </div>
          <div style={styles.tableWrap}>
            {filtered.length === 0 ? (
              <p style={{ textAlign: "center", color: "#888", padding: 30 }}>Sin resultados.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={{ background: "#1a1a1a", color: GOLD }}>
                    <th style={{ ...styles.th, textAlign: "center" }}>
                      <input type="checkbox" aria-label="Seleccionar todos los tickets visibles"
                        checked={todosVisiblesSeleccionados} onChange={toggleTodosVisibles}
                        style={styles.selectionCheck} />
                    </th>
                    {[["numeroTicket","Ticket"],["first_name","Nombre"],["last_name","Apellido"],["dni","DNI"],
                      ["tanda","Tanda"],["tipoPago","Tipo"],["used","Ingresó"]].map(([k,l]) => (
                      <th key={k} style={styles.th} onClick={() => toggleSort(k)}>{l}{arrow(k)}</th>
                    ))}
                    <th style={styles.th}>Fecha y hora</th>
                    <th style={styles.th}>Acciones</th>
                  </tr>
                  <tr style={{ background: "#303030" }}>
                    <th style={{ padding: 5 }} />
                    <th style={{ padding: 5 }} />
                    {["numeroTicket","first_name","last_name","dni","tanda","tipoPago","used"].map(key => (
                      <th key={key} style={{ padding: 5 }}>
                        {key === "used" ? (
                          <select value={columnFilters.used}
                            onChange={e => setColumnFilters(p => ({ ...p, used: e.target.value }))}
                            style={styles.columnFilter}>
                            <option value="">Todos</option>
                            <option value="si">Sí</option>
                            <option value="no">No</option>
                          </select>
                        ) : (
                          <input value={columnFilters[key]}
                            onChange={e => setColumnFilters(p => ({ ...p, [key]: e.target.value }))}
                            placeholder="Filtrar..." style={styles.columnFilter} />
                        )}
                      </th>
                    ))}
                    <th style={{ padding: 5 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((g, i) => (
                    <tr key={g.hashSeguro} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <input type="checkbox" aria-label={`Seleccionar ticket ${ticketPosition(g)}`}
                          checked={selectedHashes.includes(g.hashSeguro)}
                          onChange={() => toggleTicketSelection(g.hashSeguro)}
                          style={styles.selectionCheck} />
                      </td>
                      <td style={{ ...styles.td, fontWeight: "800", color: GOLD }}>
                        {ticketPosition(g)}
                      </td>
                      <td style={styles.td}>{g.first_name}</td>
                      <td style={styles.td}>{g.last_name}</td>
                      <td style={styles.td}>{g.dni}</td>
                      <td style={styles.td}>{g.tanda || "—"}</td>
                      <td style={styles.td}>
                        <span style={{ padding: "2px 7px", borderRadius: 5, fontSize: 11, fontWeight: "700",
                          background: g.tipoPago === "CORTESIA" ? "#e9ecef" : "#fff3cd", color: "#333" }}>
                          {g.tipoPago}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {g.used
                          ? <span style={{ color: "#198754", fontWeight: "700" }}>✓</span>
                          : <span style={{ color: "#aaa" }}>—</span>}
                      </td>
                      <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{formatIngreso(g.used)}</td>
                      <td style={styles.td}>
                        <div style={styles.ticketActions}>
                          {g.pdf_url && (
                            <a href={g.pdf_url} target="_blank" rel="noopener noreferrer" style={styles.actionLink}>
                              Descargar
                            </a>
                          )}
                          <button style={{ ...styles.actionBtn, background: "#25D366", color: "#fff" }}
                            onClick={() => compartirWhatsApp(g)}>
                            WhatsApp
                          </button>
                          {mailEnabled && (
                            <button style={{ ...styles.actionBtn, background: "#444", color: "#fff" }}
                              onClick={() => compartirEmail(g)}>
                              Email
                            </button>
                          )}
                          <button
                            style={{ ...styles.actionBtn, background: g.used ? "#eee" : "#f8d7da",
                              color: g.used ? "#aaa" : "#842029", cursor: g.used ? "not-allowed" : "pointer" }}
                            disabled={!!g.used}
                            title={g.used ? "No se puede eliminar porque ya fue utilizado" : "Eliminar ticket"}
                            onClick={() => handleEliminarTicket(g)}>
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── STATS ── */}
      {tab === "stats" && (
        <div style={{ padding: 16, maxWidth: 680, margin: "0 auto" }}>
          <div style={styles.section}>
            <h3 style={styles.title}>Mis inscriptos</h3>
            <Bar label="Ingresaron"  value={stats.ingresaron} max={stats.total} color="#198754" />
            <Bar label="Pendientes"  value={stats.pendientes} max={stats.total} color="#1976d2" />
            <Bar label="Cortesías"   value={stats.cortesias}  max={stats.total} color="#dc3545" />
          </div>
        </div>
      )}

      <div style={{ height: 40 }} />

      {/* ── Popup ticket(s) generado(s) ── */}
      {ticketsGenerados && (
        <div style={styles.popupOverlay} onClick={() => setTicketsGenerados(null)}>
          <div style={styles.popupShell} onClick={e => e.stopPropagation()}>
            <button
              aria-label="Cerrar"
              onClick={() => setTicketsGenerados(null)}
              style={styles.popupClose}>
              ×
            </button>
            <div style={styles.popupCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>✅ {ticketNombre} generado</h3>
            </div>
            <p style={{ fontSize: 13, color: "#555", marginBottom: 14 }}>{ticketsGenerados.nombre}</p>
            {ticketsGenerados.urls.length > 1 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                <button
                  style={{ ...styles.goldBtn, flex: 1, minWidth: 170, padding: "12px 18px" }}
                  disabled={!!bulkWorking}
                  onClick={descargarTodosGenerados}>
                  {bulkWorking === "generated-download"
                    ? "PREPARANDO..."
                    : `DESCARGAR TODOS (${ticketsGenerados.urls.length})`}
                </button>
                <button
                  style={{ ...styles.goldBtn, flex: 1, minWidth: 170, padding: "12px 18px",
                    background: "#25D366", color: "#fff" }}
                  disabled={!!bulkWorking}
                  onClick={compartirTodosGeneradosWhatsApp}>
                  {bulkWorking === "generated-whatsapp"
                    ? "PREPARANDO..."
                    : `WHATSAPP TODOS (${ticketsGenerados.urls.length})`}
                </button>
              </div>
            )}

            {ticketsGenerados.urls.map((url, i) => {
              const texto = `Ticket de ${ticketsGenerados.nombre} — ${eventoActivo?.name || eventoActivo?.nombre || ""}`;
              const mailLink = `mailto:?subject=${encodeURIComponent(texto)}&body=${encodeURIComponent(`${texto}\n\n${url}`)}`;

              return (
                <div key={url} style={{ marginBottom: 16 }}>
                  <iframe src={url} title={`ticket-${i}`}
                    style={{ width: "100%", height: 420, border: "1px solid #ddd", borderRadius: 10 }} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
                      <button style={{ ...styles.goldBtn, width: "100%" }}>Ver / Descargar</button>
                    </a>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button
                      onClick={() => compartirPdfPorWhatsApp({
                        url,
                        nombre: `${(ticketsGenerados.nombre || "ticket").replace(/\s+/g, "_")}_${i + 1}_de_${ticketsGenerados.urls.length}.pdf`,
                        texto,
                      })}
                      style={{ background: "#25D366", color: "#fff", border: "none", borderRadius: 8,
                        padding: "9px 14px", flex: 1, cursor: "pointer", fontSize: 13, fontWeight: "700" }}>
                      WhatsApp
                    </button>
                    {mailEnabled && (
                      <a href={mailLink} style={{ flex: 1 }}>
                        <button style={{ background: "#444", color: "#fff", border: "none", borderRadius: 8,
                          padding: "9px 14px", width: "100%", cursor: "pointer", fontSize: 13, fontWeight: "700" }}>
                          Email
                        </button>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}

      {/* Confirmaciones e ingreso de datos con estética de la empresa */}
      {dialog && (
        <div style={styles.messageOverlay} onClick={() => setDialog(null)}>
          <div style={styles.messageCard} onClick={e => e.stopPropagation()}>
            <div style={{
              ...styles.messageIcon,
              background: dialog.type === "danger" ? "#dc3545" : `${GOLD}28`,
              color: dialog.type === "danger" ? "#fff" : FONDO,
            }}>
              {dialog.type === "danger" || dialog.type === "tickets" ? "!" : "✉"}
            </div>
            <h3 style={styles.messageTitle}>{dialog.title}</h3>
            {dialog.message && <p style={styles.messageText}>{dialog.message}</p>}

            {dialog.type === "tickets" && (
              <>
                <div style={styles.confirmQuantityLabel}>CANTIDAD DE {ticketNombrePlural.toUpperCase()}</div>
                <div style={{ ...styles.confirmQuantity, color: FONDO, borderColor: GOLD }}>
                  {dialog.quantity}
                </div>
                <div style={styles.confirmDetails}>
                  {(dialog.details || []).map(([label, value]) => (
                    <div key={label} style={styles.confirmRow}>
                      <span style={styles.confirmLabel}>{label}</span>
                      <strong style={styles.confirmValue}>{value || "—"}</strong>
                    </div>
                  ))}
                </div>
                <div style={styles.confirmWarning}>
                  Revisá especialmente la cantidad. Al confirmar se crearán los QR y sus archivos PDF.
                </div>
              </>
            )}

            {dialog.type === "input" && (
              <input
                autoFocus
                type="email"
                style={{ ...styles.dialogInput, outlineColor: GOLD }}
                value={dialogInput}
                onChange={e => setDialogInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && dialogInput.trim()) dialog.onConfirm(dialogInput.trim());
                }}
                placeholder="persona@email.com"
              />
            )}

            <div style={styles.messageActions}>
              <button style={styles.modalCancel} onClick={() => setDialog(null)}>Cancelar</button>
              <button
                style={{
                  ...styles.modalConfirm,
                  background: dialog.type === "danger" ? "#dc3545" : GOLD,
                  color: dialog.type === "danger" ? "#fff" : FUENTE,
                  opacity: dialog.type === "input" && !dialogInput.trim() ? 0.5 : 1,
                }}
                disabled={dialog.type === "input" && !dialogInput.trim()}
                onClick={() => dialog.onConfirm(dialog.type === "input" ? dialogInput.trim() : undefined)}>
                {dialog.confirmLabel || "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resultado de acciones */}
      {notice && (
        <div
          style={notice.autoClose ? styles.toastWrap : styles.messageOverlay}
          onClick={() => !notice.autoClose && setNotice(null)}>
          <div
            style={notice.autoClose ? styles.toastCard : styles.messageCard}
            onClick={e => e.stopPropagation()}>
            <div style={{
              ...styles.messageIcon,
              background: notice.type === "success" ? "#d1e7dd"
                : notice.type === "warning" ? "#fff3cd" : "#dc3545",
              color: notice.type === "success" ? "#0f6848"
                : notice.type === "warning" ? "#856404" : "#fff",
            }}>
              {notice.type === "success" ? "✓" : notice.type === "warning" ? "!" : "×"}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ ...styles.messageTitle, marginBottom: 4 }}>{notice.title}</h3>
              <p style={{ ...styles.messageText, margin: 0 }}>{notice.message}</p>
            </div>
            {!notice.autoClose && (
              <button style={{ ...styles.modalConfirm, background: GOLD, color: FUENTE, marginTop: 14 }}
                onClick={() => setNotice(null)}>
                Entendido
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const makeStyles = (GOLD, FONDO, FUENTE) => ({
  page:          { background: `linear-gradient(180deg, ${GOLD}18 0, #f4f4f4 240px)`, minHeight: "100vh" },
  loadingOverlay:{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  popupOverlay:  { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  popupShell:    { position: "relative", width: "100%", maxWidth: 420 },
  popupCard:     { background: "#fff", borderRadius: 20, padding: 20, width: "100%", boxSizing: "border-box", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 8px 28px rgba(0,0,0,0.3)" },
  popupClose:    { position: "absolute", top: -10, right: -10, width: 34, height: 34, padding: 0,
    borderRadius: "50%", border: "2px solid #111", background: "#fff", color: "#111", fontSize: 23,
    lineHeight: "28px", fontWeight: "800", cursor: "pointer", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 3, boxShadow: "0 3px 9px rgba(0,0,0,.2)" },
  outlineSmall:  { background: "transparent", color: "#555", border: "1px solid #ccc", borderRadius: 25, padding: "5px 12px", cursor: "pointer", fontSize: 12, whiteSpace: "nowrap", textTransform: "uppercase" },
  header:     { background: FONDO, color: FUENTE, padding: "13px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  neutralHeader: { background: "#171717", color: "#fff", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  empty: { background: "#fff", borderRadius: 16, padding: 32, textAlign: "center", color: "#888", boxShadow: "0 2px 8px rgba(0,0,0,.06)" },
  outlineBtn: { background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 25, padding: "7px 14px", cursor: "pointer", fontSize: 13, textTransform: "uppercase" },
  chipsRow:   { display: "flex", gap: 8, padding: "12px 16px", flexWrap: "wrap" },
  chip:       { display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 14px", borderRadius: 10, minWidth: 65 },
  filtersRow: { display: "flex", flexWrap: "wrap", gap: 8, padding: "10px 16px", background: "#fff", borderBottom: "1px solid #e0e0e0" },
  bulkActions: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", margin: "14px 16px 0",
    padding: 14, background: "#fff", borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,.08)" },
  bulkBtn: { border: "none", borderRadius: 25, padding: "9px 14px", cursor: "pointer",
    fontSize: 11, fontWeight: "800", textTransform: "uppercase" },
  columnFilter: { width: "100%", minWidth: 72, boxSizing: "border-box", border: "1px solid #666",
    borderRadius: 18, padding: "6px 8px", background: "#fff", color: "#222", fontSize: 11 },
  selectionCheck: { width: 18, height: 18, cursor: "pointer", accentColor: GOLD },
  fi:         { padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, flex: 1, minWidth: 140 },
  clearBtn:   { padding: "8px 14px", borderRadius: 25, border: "none", background: "#e0e0e0", cursor: "pointer", fontSize: 13, textTransform: "uppercase" },
  select:     { padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, width: "100%", background: "#fff" },
  tableWrap:  { overflowX: "auto", padding: "12px 16px" },
  table:      { width: "100%", borderCollapse: "collapse", fontSize: 13, borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  th:         { padding: "11px 10px", textAlign: "left", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap" },
  td:         { padding: "9px 10px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" },
  section:    { background: "#fff", borderRadius: 14, padding: "14px 18px", marginBottom: 14, boxShadow: "0 2px 6px rgba(0,0,0,0.07)" },
  title:      { margin: "0 0 12px", fontSize: 14, fontWeight: "700", color: "#1a1a1a" },
  flabel:     { display: "block", fontWeight: "600", fontSize: 12, color: "#555", marginTop: 10, marginBottom: 4 },
  finput:     { padding: "9px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box", width: "100%", background: "#fafafa" },
  goldBtn:    { background: GOLD, color: FUENTE, border: "none", borderRadius: 25, padding: "9px 18px", cursor: "pointer", fontWeight: "700", fontSize: 13, textTransform: "uppercase" },
  ticketActions: { display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap", minWidth: 285 },
  actionBtn: { border: "none", borderRadius: 25, padding: "6px 11px", fontSize: 11, fontWeight: "700", textTransform: "uppercase" },
  actionLink: { display: "inline-block", borderRadius: 25, padding: "6px 11px", fontSize: 11, fontWeight: "700",
    background: GOLD, color: FUENTE, textDecoration: "none", textTransform: "uppercase" },
  messageOverlay: { position: "fixed", inset: 0, zIndex: 12000, background: "rgba(10,10,10,.62)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 18, backdropFilter: "blur(3px)" },
  messageCard: { width: "100%", maxWidth: 370, background: "#fff", borderRadius: 20, padding: 24,
    boxSizing: "border-box", boxShadow: "0 24px 70px rgba(0,0,0,.32)", textAlign: "center" },
  messageIcon: { width: 48, height: 48, borderRadius: 15, display: "flex", alignItems: "center",
    justifyContent: "center", margin: "0 auto 14px", fontSize: 25, fontWeight: "900" },
  messageTitle: { margin: "0 0 8px", color: "#1d1d1d", fontSize: 18, fontWeight: "800" },
  messageText: { margin: "0 0 18px", color: "#666", lineHeight: 1.5, fontSize: 13 },
  confirmQuantityLabel: { color: "#777", fontSize: 11, fontWeight: "900", letterSpacing: 1.1, marginTop: 14 },
  confirmQuantity: { width: 104, height: 104, margin: "8px auto 18px", border: "4px solid", borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 52, lineHeight: 1,
    fontWeight: "900", background: "#fff", boxShadow: "inset 0 3px 8px rgba(0,0,0,.08), 0 8px 24px rgba(0,0,0,.12)" },
  confirmDetails: { border: "1px solid #e5e5e5", borderRadius: 14, overflow: "hidden", textAlign: "left" },
  confirmRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
    padding: "9px 12px", borderBottom: "1px solid #eee", fontSize: 12 },
  confirmLabel: { color: "#777", flexShrink: 0, fontWeight: "700", textTransform: "uppercase" },
  confirmValue: { color: "#222", textAlign: "right", overflowWrap: "anywhere" },
  confirmWarning: { marginTop: 14, padding: "10px 12px", borderRadius: 12, background: "#fff3cd",
    color: "#755800", fontSize: 12, lineHeight: 1.4, fontWeight: "700" },
  messageActions: { display: "flex", gap: 9, marginTop: 18 },
  modalCancel: { flex: 1, padding: "11px 14px", borderRadius: 25, border: "1px solid #ddd",
    background: "#fff", color: "#555", cursor: "pointer", fontWeight: "700", textTransform: "uppercase" },
  modalConfirm: { flex: 1, padding: "11px 14px", borderRadius: 25, border: "none",
    cursor: "pointer", fontWeight: "800", textTransform: "uppercase" },
  dialogInput: { width: "100%", boxSizing: "border-box", padding: "12px 13px", borderRadius: 10,
    border: "1px solid #d5d5d5", background: "#fafafa", fontSize: 14 },
  toastWrap: { position: "fixed", right: 18, bottom: 18, zIndex: 12000, width: "calc(100% - 36px)", maxWidth: 360 },
  toastCard: { background: "#fff", borderRadius: 16, padding: "14px 16px", boxShadow: "0 14px 38px rgba(0,0,0,.24)",
    display: "flex", alignItems: "center", gap: 12, textAlign: "left" },
});
